import React, { useEffect, useState } from 'react';
import { loadImage, getLoadedImage } from '../../utils/imageCache';

const RETRY_AFTER_FAIL_MS = 8000;

// 先読みした画像（utils/imageCache.js）を表示に使う。まだ読めていなければ読み込み、
// 失敗しても一定間隔と通信の復帰時に読み直す（一度失敗しても「画像がない」のままにしない）。
// 返り値：{ src＝表示に使う URL（まだなら null）, failed＝再試行しても読めていない }
export function useCachedImage(url) {
  const [state, setState] = useState(() => ({ url, src: getLoadedImage(url), failed: false }));

  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    let timer = null;
    const ready = getLoadedImage(url);
    setState({ url, src: ready, failed: false });
    if (ready) return undefined;
    const attempt = () => {
      loadImage(url).then(
        (src) => { if (alive) setState({ url, src, failed: false }); },
        () => {
          if (!alive) return;
          setState({ url, src: null, failed: true });
          timer = setTimeout(attempt, RETRY_AFTER_FAIL_MS);
        },
      );
    };
    attempt();
    const onOnline = () => {
      if (getLoadedImage(url)) return;
      clearTimeout(timer);
      attempt();
    };
    window.addEventListener('online', onOnline);
    return () => {
      alive = false;
      clearTimeout(timer);
      window.removeEventListener('online', onOnline);
    };
  }, [url]);

  // url が変わった直後は、前の url の結果を返さない
  if (state.url !== url) return { src: getLoadedImage(url), failed: false };
  return state;
}

// <img> の代わりに使う。読み込むまでは同じ大きさの枠に「読み込んでいます」を出す
export default function CachedImage({ src, alt, className = '' }) {
  const { src: ready, failed } = useCachedImage(src);
  if (ready) return <img className={className} src={ready} alt={alt} />;
  return (
    <div className={`${className} cached-image__placeholder`.trim()} role="img" aria-label={alt}>
      {failed ? '画像を読み込めませんでした。通信が戻ると表示されます' : '画像を読み込んでいます…'}
    </div>
  );
}
